import React from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'
import { PrimaryButton } from 'shared/components/button'
import CheckboxRhf from 'shared/components/checkbox-rhf'
import ErrorMessageRhf from 'shared/components/error-message-rhf'
import FormInputRhf from 'shared/components/form-input-rhf'
import { AuthFormHeader } from 'modules/authentication/components/auth-form-header'
import { AuthFormLayout } from 'modules/authentication/components/auth-form-layout'
import { DEFAULT_AUTH_FORM_CLASSES } from 'modules/authentication/const/default-classes'
import useLoginForm from 'modules/authentication/login/hooks/use-login-form'

export const LoginFormDashboard = ({ hasCustomDomain }: { hasCustomDomain?: boolean }) => {
  const { t } = useTranslation()

  const { formRef, handleSubmit, control, setValue, errors, isLoading, onSubmit } = useLoginForm({
    hasCustomDomain,
  })

  return (
    <>
      <AuthFormHeader title={t('security.login.title')} />
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <AuthFormLayout
          body={
            <>
              <FormInputRhf
                type="email"
                name="email"
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('security.login.username')}
                control={control}
                setValue={setValue}
                maxLength={180}
                error={errors.email?.message}
                rules={{ required: t('login.validation.email') }}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
              />
              <FormInputRhf
                type="password"
                name="password"
                setValue={setValue}
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('security.login.password')}
                control={control}
                maxLength={180}
                error={errors.password?.message}
                rules={{ required: t('login.validation.password') }}
                autoComplete="current-password"
                autoCapitalize="none"
                spellCheck={false}
              />
              <ErrorMessageRhf errors={errors} fieldName="root" />

              <div className="flex flex-wrap items-center justify-between gap-1.5 gap-x-5 sm:flex-row sm:items-start md:gap-3">
                <CheckboxRhf
                  iconClassName={DEFAULT_AUTH_FORM_CLASSES.CHECKBOX_ICON}
                  labelClassName={DEFAULT_AUTH_FORM_CLASSES.CHECKBOX_LABEL}
                  className={DEFAULT_AUTH_FORM_CLASSES.CHECKBOX}
                  name="rememberMe"
                  setValue={setValue}
                  control={control}
                  label={t('security.login.stay_connected')}
                />
                <Link
                  className="text-base text-brand-primary 2xl:text-lg"
                  href={'/restore-password'}
                >
                  {t('login.forgetpassword')}
                </Link>
              </div>
            </>
          }
          footer={
            <>
              <PrimaryButton
                isLoading={isLoading}
                type="submit"
                className={DEFAULT_AUTH_FORM_CLASSES.BUTTON}
              >
                {t('security.login.submit')}
              </PrimaryButton>
              <p
                className={twMerge(
                  DEFAULT_AUTH_FORM_CLASSES.FOOTER_HELP_TEXT_BASE,
                  'text-neutral-300',
                )}
              >
                <span className="block md:inline">{t('user.login.text.dont_have_account')} </span>
                <Link className="text-brand-primary" href={'/register'}>
                  {t('user.login.create_account_link')}
                </Link>
              </p>
            </>
          }
        />
      </form>
    </>
  )
}
